import { addDays, type ISODate } from './dates';

/**
 * Customer list and commercial terms (rules only; storage and permissions are on the server).
 * No accounting: the app records who the customer is, which roles companies have in a project and which
 * terms apply to billing, but it does not create invoices, ledgers or payment reminders.
 */

/**
 * Status of a company as the team uses it in its contact list.
 * prospect potenzieller Kunde, offered Angebot abgegeben, active aktiver Kunde, former ehemaliger Kunde,
 * partner Geschäftspartner, do_not_contact nicht mehr kontaktieren.
 */
export const COMPANY_STATUSES = ['prospect', 'offered', 'active', 'former', 'partner', 'do_not_contact'] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

/** company: Unternehmen, public: öffentliche Hand (Leitweg-ID), person: Privatperson. */
export const COMPANY_TYPES = ['company', 'public', 'person'] as const;
export type CompanyType = (typeof COMPANY_TYPES)[number];

export const PARTY_ROLES = ['auftraggeber', 'rechnungsempfaenger', 'bauherr', 'generalplaner', 'subplaner'] as const;
export type PartyRole = (typeof PARTY_ROLES)[number];

export const CONTACT_ROLES = ['invoice', 'technical', 'sn_approver'] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export const BILLING_METHODS = ['hourly', 'hoai', 'fixed_fee', 'mixed'] as const;
export type BillingMethod = (typeof BILLING_METHODS)[number];

export const DELIVERY_CHANNELS = ['email', 'post', 'portal'] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

export const ITEM_KINDS = ['nachtrag', 'nebenkosten'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

export const ITEM_STATUSES = ['offered', 'approved', 'rejected', 'invoiced'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/** Tax scenarios of a contract: the approved list is given by the tax advisor; until then only the domestic standard case and "open". */
export const TAX_SCENARIOS = ['de_standard', 'pending_advisor'] as const;
export type TaxScenario = (typeof TAX_SCENARIOS)[number];

/** Written forms of the statuses in the lists of the team (German, English, Turkish), for the import of a contact list. */
const STATUS_ALIASES: Record<CompanyStatus, string[]> = {
  prospect: ['prospect', 'interessent', 'potenzieller kunde', 'potentieller kunde', 'potansiyel musteri', 'potansiyel'],
  offered: ['offered', 'angebot abgegeben', 'angebot erstellt', 'angebot', 'teklif verildi', 'teklif'],
  active: ['active', 'active customer', 'aktiv', 'aktiver kunde', 'aktif musteri', 'aktif'],
  former: ['former', 'former customer', 'inactive', 'ehemalig', 'ehemaliger kunde', 'inaktiv', 'eski musteri', 'eski'],
  partner: ['partner', 'business partner', 'geschaftspartner', 'is ortagi'],
  do_not_contact: ['do not contact', 'dnc', 'nicht kontaktieren', 'nicht mehr kontaktieren', 'iletisime gecilmeyecek'],
};

/** Status from a text of any language; null if not recognized. */
export function parseCompanyStatus(text: string | null | undefined): CompanyStatus | null {
  const t = (text ?? '')
    .trim()
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return null;
  for (const s of COMPANY_STATUSES) if (t === s.replace(/_/g, ' ')) return s;
  for (const [status, aliases] of Object.entries(STATUS_ALIASES) as [CompanyStatus, string[]][]) if (aliases.includes(t)) return status;
  return null;
}

/* ------------------------------------------------------------------ */
/* Tax number format                                                    */
/* ------------------------------------------------------------------ */

export interface TaxIdCheck {
  status: 'empty' | 'ok' | 'bad';
  kind: 'vat' | 'tax' | null;
  normalized: string;
}

/** Check digit of a German USt-IdNr (ISO 7064, MOD 11,10). `digits`: the 9 digits after "DE". */
function deVatCheckDigitOk(digits: string): boolean {
  let product = 10;
  for (let i = 0; i < 8; i++) {
    let sum = (Number(digits[i]) + product) % 10;
    if (sum === 0) sum = 10;
    product = (2 * sum) % 11;
  }
  let check = 11 - product;
  if (check === 10) check = 0;
  return check === Number(digits[8]);
}

/**
 * Format check of a VAT ID or tax number. Not a lookup: the tax advisor remains responsible for the content, the
 * check only catches typos. DE: USt-IdNr "DE" + 9 digits with check digit, tax number with 10, 11 or 13 digits.
 * TR: tax number (10) or ID (11 digits). Other countries: two-letter prefix and 2 to 12 characters.
 */
export function checkTaxId(raw: string | null | undefined, country = 'DE'): TaxIdCheck {
  const value = (raw ?? '').trim();
  if (!value) return { status: 'empty', kind: null, normalized: '' };
  const compact = value.replace(/[\s./-]/g, '').toUpperCase();
  const c = country.toUpperCase();
  const prefixed = /^[A-Z]{2}/.test(compact);
  if (prefixed) {
    if (compact.startsWith('DE')) {
      const d = compact.slice(2);
      return { status: /^\d{9}$/.test(d) && deVatCheckDigitOk(d) ? 'ok' : 'bad', kind: 'vat', normalized: compact };
    }
    return { status: /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(compact) ? 'ok' : 'bad', kind: 'vat', normalized: compact };
  }
  if (!/^\d+$/.test(compact)) return { status: 'bad', kind: 'tax', normalized: compact };
  if (c === 'TR') return { status: compact.length === 10 || compact.length === 11 ? 'ok' : 'bad', kind: 'tax', normalized: compact };
  if (c === 'DE') return { status: [10, 11, 13].includes(compact.length) ? 'ok' : 'bad', kind: 'tax', normalized: compact };
  return { status: compact.length >= 5 && compact.length <= 15 ? 'ok' : 'bad', kind: 'tax', normalized: compact };
}

/* ------------------------------------------------------------------ */
/* Company names                                                        */
/* ------------------------------------------------------------------ */

const LEGAL_FORMS = new Set(['gmbh', 'mbh', 'ag', 'kg', 'ohg', 'gbr', 'ug', 'ek', 'ev', 'se', 'co', 'und', 'and', 'sti', 'as', 'ltd', 'inc', 'sa', 'srl', 'bv', 'nv']);

function fold(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** Name without legal form, case, punctuation and umlauts: the base of the duplicate suggestion. */
export function normalizeCompanyName(name: string): string {
  const words = fold(name)
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w && !LEGAL_FORMS.has(w));
  return words.join(' ');
}

function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[b.length];
}

export interface GroupCandidate {
  key: string;
  name: string;
}

export interface CompanyGroup {
  /** normalized base name of the first member */
  base: string;
  members: string[];
}

/**
 * Suggests which spellings may belong to one company (Stadtwerke X GmbH / Stadtwerke X). Only a suggestion: the
 * same name is no reason to merge automatically (two companies can have the same name), the office confirms each
 * group. Names differing only by case, legal form, punctuation, umlauts or one typo (names of 8+ letters) are grouped.
 */
export function suggestCompanyGroups(candidates: GroupCandidate[]): CompanyGroup[] {
  const norm = candidates.map((c) => ({ ...c, n: normalizeCompanyName(c.name) }));
  const groups: { base: string; members: string[] }[] = [];
  for (const c of norm) {
    if (!c.n) {
      groups.push({ base: c.name, members: [c.key] });
      continue;
    }
    const g = groups.find((x) => x.base === c.n || (x.base.length >= 8 && c.n.length >= 8 && editDistance(x.base, c.n, 1) <= 1));
    if (g) g.members.push(c.key);
    else groups.push({ base: c.n, members: [c.key] });
  }
  return groups;
}

/* ------------------------------------------------------------------ */
/* Completeness of commercial terms                                     */
/* ------------------------------------------------------------------ */

export const GAP_CODES = [
  'recipient',
  'address',
  'payment_days',
  'billing_method',
  'tax_scenario',
  'delivery',
  'vat_id',
  'leitweg',
  'discount',
] as const;
export type GapCode = (typeof GAP_CODES)[number];

export interface GapTerms {
  recipientCompanyId: string | null;
  recipientAddress: string | null;
  paymentDays: number | null;
  discountPercent: number | null;
  discountDays: number | null;
  billingMethod: BillingMethod | null;
  taxScenario: string | null;
  deliveryChannel: DeliveryChannel | null;
  deliveryAddress: string | null;
}

export interface GapCompany {
  type: CompanyType;
  country: string;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  vatId: string | null;
}

/** Which data the later invoice draft still needs. `leitweg`: the recipient's Leitweg-ID or the buyer reference of the terms. */
export function contractGaps(terms: GapTerms | null, recipient: GapCompany | null, opts: { leitwegId?: string | null; buyerRef?: string | null } = {}): GapCode[] {
  const gaps: GapCode[] = [];
  if (!terms || !terms.recipientCompanyId || !recipient) gaps.push('recipient');
  else {
    if (!terms.recipientAddress?.trim() && !(recipient.street?.trim() && recipient.city?.trim())) gaps.push('address');
    if (recipient.type !== 'person' && checkTaxId(recipient.vatId, recipient.country).status !== 'ok') gaps.push('vat_id');
    if (recipient.type === 'public' && !opts.leitwegId?.trim() && !opts.buyerRef?.trim()) gaps.push('leitweg');
  }
  if (!terms || terms.paymentDays === null || terms.paymentDays === undefined) gaps.push('payment_days');
  if (terms && terms.discountPercent) {
    if (!terms.discountDays || (terms.paymentDays !== null && terms.discountDays >= terms.paymentDays)) gaps.push('discount');
  }
  if (!terms || !terms.billingMethod) gaps.push('billing_method');
  if (!terms || !terms.taxScenario) gaps.push('tax_scenario');
  if (!terms || !terms.deliveryChannel || (terms.deliveryChannel !== 'portal' && !terms.deliveryAddress?.trim())) gaps.push('delivery');
  return GAP_CODES.filter((g) => gaps.includes(g));
}

/** Sum of the supplements and ancillary costs that count: commissioned (approved) or already invoiced. Offered and rejected items do not count. */
/**
 * Postal address of a firm as lines for a document (street, postal code and city, the country when it is not the
 * country of the issuer). Empty lines are left out.
 */
export function companyAddressLines(c: { street?: string | null; postalCode?: string | null; city?: string | null; country?: string | null }, homeCountry = 'DE'): string[] {
  const place = [c.postalCode?.trim(), c.city?.trim()].filter(Boolean).join(' ');
  const country = c.country && c.country.toUpperCase() !== homeCountry.toUpperCase() ? c.country.toUpperCase() : '';
  return [c.street?.trim() ?? '', place, country].filter((x) => !!x);
}

/** Name of a contact person for a document: "Dr. Anna von Berg" (first and last name). */
export function contactDisplayName(c: { firstName?: string | null; lastName: string }): string {
  return [c.firstName?.trim(), c.lastName.trim()].filter(Boolean).join(' ');
}

export function contractItemsTotal(items: { amountCents: number; status: ItemStatus }[]): number {
  return items.filter((i) => i.status === 'approved' || i.status === 'invoiced').reduce((s, i) => s + i.amountCents, 0);
}

/** Due date: document date plus payment days. */
export function dueDateFor(date: ISODate, days: number): ISODate {
  return addDays(date, days);
}

/* ------------------------------------------------------------------ */
/* Payment terms: due date, discount (Skonto) and deductions after VAT  */
/* ------------------------------------------------------------------ */

/**
 * A deduction after the VAT-inclusive total, agreed with some customers (Nachlass, Sicherheitseinbehalt, ...).
 * percent: share of the gross total in percent (two decimals), amount: fixed amount in cents.
 */
export interface PaymentAdjustment {
  label: string;
  mode: 'percent' | 'amount';
  value: number;
}

export interface PaymentTerms {
  /** the invoice is due this many days after the document date (null: not stated) */
  paymentDays: number | null;
  /** discount in percent when paid within `discountDays` (e.g. 1.5 within 7 days) */
  discountPercent?: number | null;
  discountDays?: number | null;
  /** deductions from the gross total, in the given order */
  adjustments?: PaymentAdjustment[];
}

/**
 * Highest Skonto in percent. One value for the form, the input check of the server and the database (CHECK of
 * project_contract.discount_percent, migration 0004): a higher value is a typing error (DATA-02).
 */
export const MAX_DISCOUNT_PERCENT = 30;

/** Payment terms as they are stored: every value present, adjustments cleaned. */
export interface StoredPaymentTerms {
  paymentDays: number | null;
  discountPercent: number | null;
  discountDays: number | null;
  adjustments: PaymentAdjustment[];
}

/**
 * Checks and cleans payment terms entered by hand (project terms or one document): a discount needs percent and days,
 * its period must be shorter than the payment term, a percentage deduction cannot exceed 100. Deductions without a
 * label or without a value are dropped; labels are cut to 80 characters.
 */
export function normalizePaymentTerms(input: PaymentTerms): { ok: true; terms: StoredPaymentTerms } | { ok: false; field: 'discountPercent' | 'discountDays' | 'adjustments'; message: string } {
  const dp = input.discountPercent ?? null;
  const dd = input.discountDays ?? null;
  const pd = input.paymentDays ?? null;
  if ((dp && !dd) || (dd && !dp)) return { ok: false, field: 'discountDays', message: 'discount needs percent and days' };
  if (dp !== null && (dp < 0 || dp > MAX_DISCOUNT_PERCENT)) return { ok: false, field: 'discountPercent', message: `discount percent above ${MAX_DISCOUNT_PERCENT}` };
  if (dd !== null && pd !== null && dd >= pd) return { ok: false, field: 'discountDays', message: 'discount period must be shorter than the payment term' };
  const adjustments = (input.adjustments ?? []).filter((a) => a.label.trim() && a.value > 0).map((a) => ({ label: a.label.trim().slice(0, 80), mode: a.mode, value: a.value }));
  if (adjustments.some((a) => a.mode === 'percent' && a.value > 100)) return { ok: false, field: 'adjustments', message: 'percent above 100' };
  return { ok: true, terms: { paymentDays: pd, discountPercent: dp, discountDays: dd, adjustments } };
}

export interface PaymentBreakdown {
  netCents: number;
  vatCents: number;
  grossCents: number;
  adjustments: { label: string; amountCents: number }[];
  /** gross total minus the deductions after VAT: what the customer pays at the latest on the due date */
  payableCents: number;
  discount: { percent: number; days: number; amountCents: number; payableCents: number } | null;
}

/**
 * Amounts from net total, VAT rate and payment terms. Order: net + VAT = gross; deductions after VAT reduce the gross
 * total (each on the gross total, not on each other); the discount for early payment is taken from the remaining
 * payable amount. Whether and how the VAT is corrected for a discount is decided by the tax advisor (§ 17 UStG);
 * this calculation shows the amounts the customer pays and does not replace the invoice.
 */
export function paymentBreakdown(netCents: number, vatPercent: number, terms: PaymentTerms | null): PaymentBreakdown {
  const vatCents = Math.round((netCents * (vatPercent || 0)) / 100);
  const grossCents = netCents + vatCents;
  const adjustments = (terms?.adjustments ?? [])
    .filter((a) => a.value > 0)
    .map((a) => ({ label: a.label, amountCents: -Math.min(grossCents, a.mode === 'percent' ? Math.round((grossCents * a.value) / 100) : Math.round(a.value)) }));
  const payableCents = Math.max(0, grossCents + adjustments.reduce((s, a) => s + a.amountCents, 0));
  const p = terms?.discountPercent ?? 0;
  const d = terms?.discountDays ?? 0;
  const discountAmount = p > 0 && d > 0 ? Math.round((payableCents * p) / 100) : 0;
  return {
    netCents,
    vatCents,
    grossCents,
    adjustments,
    payableCents,
    discount: p > 0 && d > 0 ? { percent: p, days: d, amountCents: discountAmount, payableCents: payableCents - discountAmount } : null,
  };
}

function pct(n: number, lang: 'de' | 'en'): string {
  return n.toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function dmy(d: ISODate, lang: 'de' | 'en'): string {
  const [y, m, day] = d.split('-');
  return lang === 'de' ? `${day}.${m}.${y}` : `${day}/${m}/${y}`;
}

/**
 * Sentence for the document: due date and discount, e.g. "Zahlbar innerhalb von 14 Tagen netto (bis 22.10.2026).
 * Bei Zahlung innerhalb von 7 Tagen (bis 15.10.2026) 1,5 % Skonto." Empty if the terms state nothing.
 */
export function paymentTermText(terms: PaymentTerms | null, documentDate: ISODate, lang: 'de' | 'en' = 'de'): string {
  if (!terms) return '';
  const parts: string[] = [];
  if (terms.paymentDays !== null && terms.paymentDays !== undefined) {
    const due = dmy(dueDateFor(documentDate, terms.paymentDays), lang);
    parts.push(
      lang === 'de'
        ? `Zahlbar innerhalb von ${terms.paymentDays} Tagen ohne Abzug (bis ${due}).`
        : `Payable within ${terms.paymentDays} days without deduction (by ${due}).`,
    );
  }
  if (terms.discountPercent && terms.discountDays) {
    const until = dmy(dueDateFor(documentDate, terms.discountDays), lang);
    parts.push(
      lang === 'de'
        ? `Bei Zahlung innerhalb von ${terms.discountDays} Tagen (bis ${until}) ${pct(terms.discountPercent, lang)} % Skonto.`
        : `${pct(terms.discountPercent, lang)}% discount if paid within ${terms.discountDays} days (by ${until}).`,
    );
  }
  return parts.join(' ');
}
